import InfoIcon from '@mdi/svg/svg/information-outline.svg?react';
import {
  DatePicker,
  dateToDay,
  endOfDayToDate,
  Flex,
  Grid,
  IconWithTooltip,
  Input,
  Radio,
  RadioGroupWrapper,
} from '@solinkcorp/solink-components';
import classNames from 'classnames';
import { CommonContext } from 'components/Contexts';
import moment from 'moment';
import React, { useContext } from 'react';
import useLocale from 'src/hooks/useLocale';
import { MAX_HOURLY_OCCURRENCES_PER_2_YEARS } from 'vd-constants';
import styles from './ActivityExpiryControl.module.sass'; // Importing the CSS module
import { AI_INSPECTION_END_TYPE, type AiInspectionEndType } from './ScheduledActivityForm';

interface ActivityExpiryControlProps {
  selectedEndType: AiInspectionEndType;
  setSelectedEndType: React.Dispatch<React.SetStateAction<AiInspectionEndType>>;
  timezoneId: string;
  startTime: number;
  endTime: number;
  updateEndTime: (date: Date | undefined) => void;
  reoccurringCount: number;
  setReoccurringCount: React.Dispatch<React.SetStateAction<number>>;
  reinitializingEndTime: number;
  disabled?: boolean;
  hasImageCreditsError: boolean;
  disableEditExpirationType?: boolean;
  occurrencesTooltip?: string;
}

const ActivityExpiryControl: React.FC<ActivityExpiryControlProps> = ({
  selectedEndType,
  setSelectedEndType,
  startTime,
  timezoneId,
  updateEndTime,
  endTime,
  setReoccurringCount,
  reinitializingEndTime,
  reoccurringCount,
  disabled,
  hasImageCreditsError,
  disableEditExpirationType = false,
  occurrencesTooltip,
}) => {
  const {
    authData: { firstDayOfWeek },
  } = useContext(CommonContext);
  const locale = useLocale();

  const handleRadioChange = ({ value }: { value: string | number }) => {
    setSelectedEndType(value as AiInspectionEndType);
  };

  const onDateChange = (day: string | undefined) => {
    if (!day) {
      updateEndTime(new Date(reinitializingEndTime));
    } else {
      updateEndTime(endOfDayToDate(day, timezoneId));
    }
  };

  const expirationControlClassnames = classNames(styles.activityExpirationControl, {
    [styles.disabled]: disabled,
  });

  const maxDate = dateToDay(moment().add(2, 'year').toDate(), timezoneId);

  return (
    <div className={expirationControlClassnames}>
      <RadioGroupWrapper>
        <Grid
          gridTemplateColumns="50px 200px"
          gap="12px"
          className={styles.activityExpirationRadioWrapper}
        >
          <Radio
            colorMode="dark"
            checkedValue={selectedEndType}
            value={AI_INSPECTION_END_TYPE.date}
            labelText={locale.general.on}
            handleOnChange={handleRadioChange}
            className={styles.activityExpirationRadio}
            disabled={disableEditExpirationType}
          />
          <DatePicker
            calendarStartDay={firstDayOfWeek}
            locale={locale}
            onChange={onDateChange}
            value={dateToDay(endTime, timezoneId)}
            maxDate={maxDate}
            minDate={dateToDay(startTime, timezoneId)}
            disabled={disableEditExpirationType || selectedEndType !== AI_INSPECTION_END_TYPE.date}
          />
        </Grid>
        <Grid
          gridTemplateColumns="50px 200px"
          gap="12px"
          className={styles.activityExpirationRadioWrapper}
          style={{ marginTop: '12px' }}
        >
          <Radio
            colorMode="dark"
            checkedValue={selectedEndType}
            value={AI_INSPECTION_END_TYPE.occurrence}
            labelText={locale.general.after}
            handleOnChange={handleRadioChange}
            className={styles.activityExpirationRadio}
            disabled={disableEditExpirationType}
          />
          <Flex gap="12px" alignItems="center">
            <Input
              disabled={
                disableEditExpirationType || selectedEndType !== AI_INSPECTION_END_TYPE.occurrence
              }
              className={styles.endReoccurrencesInput}
              id="recoccurences"
              value={reoccurringCount}
              type="number"
              onChange={(e) => setReoccurringCount(parseInt(e.target.value))}
              min={1}
              max={MAX_HOURLY_OCCURRENCES_PER_2_YEARS}
            />
            <label htmlFor="occurences">{locale.scheduledAIChat.occurrences}</label>
            {occurrencesTooltip && (
              <IconWithTooltip
                title={occurrencesTooltip}
                icon={InfoIcon}
                fill="var(--color-dark-gray-lightest)"
                iconSize={20}
              />
            )}
          </Flex>
        </Grid>
        <Flex
          flexDirection="row"
          className={styles.activityExpirationRadioWrapper}
          style={{ marginTop: '12px' }}
          alignItems="center"
          height="36px"
        >
          <Radio
            colorMode="dark"
            checkedValue={selectedEndType}
            value={AI_INSPECTION_END_TYPE.never}
            labelText="Never"
            handleOnChange={handleRadioChange}
            className={styles.activityExpirationRadio}
            disabled={disableEditExpirationType}
          />
        </Flex>
        {hasImageCreditsError && (
          <p className={styles.error}>{locale.scheduledAIChat.negativeImageCreditsError}</p>
        )}
      </RadioGroupWrapper>
    </div>
  );
};

export default ActivityExpiryControl;
